import React from "react";
import Icons from "./Icons";

// ─── Form Styles ───
export const inputStyle: React.CSSProperties = { width: "100%", padding: "clamp(9px, 1.2vw, 12px) clamp(12px, 1.8vw, 14px)", border: "2px solid #e5e7eb", borderRadius: "clamp(8px, 1vw, 10px)", fontSize: "clamp(14px, 0.2vw + 13px, 16px)", outline: "none", transition: "border-color 0.2s", boxSizing: "border-box", fontFamily: "inherit" };
export const selectStyle: React.CSSProperties = { ...inputStyle, background: "#fff", appearance: "auto" as const };
export const btnPrimary: React.CSSProperties = { background: "#2d5a27", color: "#fff", border: "none", borderRadius: "clamp(8px, 1vw, 10px)", padding: "clamp(9px, 1.2vw, 12px) clamp(14px, 2.2vw, 20px)", fontSize: "clamp(14px, 0.2vw + 13px, 16px)", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" };
export const btnSecondary: React.CSSProperties = { ...btnPrimary, background: "#f3f4f6", color: "#374151" };
export const labelStyle: React.CSSProperties = { fontSize: "clamp(11px, 0.1vw + 10.5px, 12px)", fontWeight: 600, color: "#6b7280", textTransform: "uppercase" as const, letterSpacing: "0.05em", marginBottom: 6, display: "block" };

// ─── Modal Component ───
export function Modal({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode; wide?: boolean }) {
  if (!open) return null;
  const mobile = typeof window !== "undefined" && window.innerWidth < 768;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: (mobile ? "flex-end" : "center") as "center" | "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: mobile ? "20px 20px 0 0" : 16, padding: mobile ? "24px 20px" : 28, width: mobile ? "100%" : (wide ? 600 : 440), maxWidth: mobile ? "100%" : "92vw", maxHeight: mobile ? "90vh" : "85vh", overflow: "auto", boxShadow: "0 25px 50px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icons.X style={{ width: 16, height: 16 }} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Card Component ───
export function Card({ children, style, onClick }: { children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void }) {
  return <div onClick={onClick} style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid #eee", ...style }}>{children}</div>;
}

// ─── Error Boundary ───
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{ padding: 32, textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <Icons.Alert style={{ width: 32, height: 32, color: "#dc2626" }} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Something went wrong</h2>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 16px" }}>{this.state.error?.message || "An unexpected error occurred."}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ background: "#2d5a27", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
